/**
 * blocks.tsx — the canvas vocabulary, as components.
 *
 * A CANVAS IS BLOCKS SEPARATED BY SPACE, never cards and no longer even rules. There is no box,
 * no shadow, no background and no divider on this surface: hierarchy comes from a 48px trough, an
 * uppercase eyebrow and the air underneath it, which is what lets six areas of wildly different
 * density read as one document rather than as a stack of tables.
 * The class names are the prototype's, prefixed `sd-` — and every one of them is written here as
 * a LITERAL, because Tailwind purges a component-layer rule whose name appears in no source file.
 *
 * EVERY BLOCK CARRIES THE JUMP ID ITS MANIFEST ROW NAMES (`ROW_DEFS[].block`). That id is the
 * whole contract between the rail and the canvas: a refusal that lands on `veh-odometer` scrolls
 * to `#b-odometer` and puts the caret in `#f-odometer`, and a block that forgets its id turns a
 * server sentence into a dead link.
 */

import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

// ─── The block shell ─────────────────────────────────────────────────────────

export function Blocks({ children }: { children: ReactNode }) {
    return <div className="sd-blocks">{children}</div>;
}

interface BlockProps {
    /** The `#b-…` id its manifest row jumps to. */
    id?: string;
    /** The uppercase eyebrow. */
    title: string;
    /**
     * `· Required`, `· records, not requirements`, `· never printed`.
     *
     * NEVER `· Optional` ON THE TWO CONDITIONAL AGREEMENTS. The deposit and insurance-loan blocks
     * pass `Required` while their subject exists and `undefined` while it does not, because the
     * word "optional" may not render for either of them in any state (owner's ruling) — a dormant
     * block says what wakes it in its lede instead of wearing a badge that reads as "skippable".
     */
    aside?: string;
    /** Unsaved local edits exist on this block. */
    unsaved?: boolean;
    /** The last commit was refused. */
    rejected?: string;
    children: ReactNode;
}

export function Block({ id, title, aside, unsaved, rejected, children }: BlockProps) {
    return (
        <section className="sd-block" id={id}>
            <div className="sd-eyebrow">
                <span>{title}</span>
                {aside && <span className="sd-opt">· {aside}</span>}
                {unsaved && <span className="sd-unsaved">· Unsaved</span>}
                {rejected && <span className="sd-rejected">· {rejected}</span>}
            </div>
            {children}
        </section>
    );
}

/** The block's opening sentence — the state of the thing, in words. */
export function Lede({ children, dim }: { children: ReactNode; dim?: boolean }) {
    return <p className={cn('sd-lede', dim && 'sd-dim')}>{children}</p>;
}

/**
 * The quiet line under a block: what this value prints on, what an empty one means.
 *
 * `id` IS FOR THE HINTS THAT ARE SOMEBODY'S DESCRIPTION. Most are free-standing prose, but a hint
 * raised BY a control — the read-out under `Read the dates from the certificate on file` — has to
 * be nameable so that control can point `aria-describedby` at it; a screen reader otherwise reads
 * the link and never reaches the sentence the link just wrote.
 */
export function Hint({
    children,
    className,
    id,
}: {
    children: ReactNode;
    className?: string;
    id?: string;
}) {
    return (
        <p className={cn('sd-hint', className)} id={id}>
            {children}
        </p>
    );
}

export function BlockActions({ children }: { children: ReactNode }) {
    return <div className="sd-b-actions">{children}</div>;
}

/**
 * An in-page jump that reads as prose, not as a button.
 *
 * NAMED PROP, NOT A SPREAD. `aria-describedby` is here because a link that writes a sentence
 * elsewhere on the page has to own it; opening the whole of `button`'s props to get it would let
 * any caller reach past the two the canvas actually wants to vary.
 */
export function LinkLike({
    onClick,
    children,
    'aria-describedby': describedBy,
}: {
    onClick: () => void;
    children: ReactNode;
    'aria-describedby'?: string;
}) {
    return (
        <button type="button" className="sd-linkish" onClick={onClick} aria-describedby={describedBy}>
            {children}
        </button>
    );
}

// ─── Record rows ─────────────────────────────────────────────────────────────

export function Rows({ children, className }: { children: ReactNode; className?: string }) {
    return <div className={cn('sd-rows', className)}>{children}</div>;
}

interface RecProps {
    label: ReactNode;
    /** Null or an empty string prints the empty text in muted rather than a blank row. */
    value: ReactNode;
    sub?: ReactNode;
    /** What an absent value says. Defaults to "Not on file". */
    empty?: string;
}

export function Rec({ label, value, sub, empty = 'Not on file' }: RecProps) {
    const absent = value === null || value === undefined || value === '';
    return (
        <div className="sd-rec">
            <div className="sd-rec-k">{label}</div>
            <div className={cn('sd-rec-v', absent && 'sd-empty')}>
                {absent ? empty : value}
                {sub && <span className="sd-sub">{sub}</span>}
            </div>
        </div>
    );
}

// ─── Document slots — the driver file, and anything else that is a file or nothing ──

interface SlotProps {
    name: string;
    meta: ReactNode;
    /** Not on file: the name goes muted and the row carries no urgency. */
    absent?: boolean;
    /** On file but expired — the only place a record row is allowed to go destructive. */
    bad?: boolean;
    action?: ReactNode;
}

export function Slot({ name, meta, absent, bad, action }: SlotProps) {
    return (
        <div className={cn('sd-slot', absent && 'sd-slot-absent', bad && 'sd-slot-bad')}>
            <div>
                <div className="sd-slot-n">{name}</div>
                <div className="sd-slot-m">{meta}</div>
            </div>
            {action}
        </div>
    );
}

// ─── Signature blocks ────────────────────────────────────────────────────────

interface SignatureProps {
    name: string;
    role: string;
    /** `Signed 09:14` / `Not signed` / `Countersigns last`. */
    state: string;
    /** The state is a wait on somebody else — the only amber on the canvas. */
    waiting?: boolean;
    signed?: boolean;
    /** `signed 09:14 · IP 154.160.…` — the evidence line under the rule. */
    evidence?: ReactNode;
    /** What is holding this signature up, in words. */
    note?: ReactNode;
}

/**
 * One party's place on the instrument, drawn as the instrument draws it.
 *
 * A DASHED RULE WITH A CAPTION IS WHAT A SIGNATURE BLOCK LOOKS LIKE ON PAPER, and that is the
 * point: this is the only surface in the app where an admin is looking at a legal document rather
 * than at a record of one. A signed block goes solid and prints its evidence; an unsigned one
 * stays dashed and says who it is waiting for.
 */
export function SignatureBlock({
    name,
    role,
    state,
    waiting,
    signed,
    evidence,
    note,
}: SignatureProps) {
    return (
        <div className={cn('sd-sig', signed && 'sd-sig-signed')}>
            <div className="sd-sig-top">
                <span className="sd-sig-name">{name}</span>
                <span className="sd-sig-role">{role}</span>
                <span className={cn('sd-sig-state', waiting && 'sd-sig-state-wait')}>{state}</span>
            </div>
            <div className="sd-sig-rule" />
            <div className="sd-sig-cap">Signature</div>
            {evidence && <div className="sd-sig-ev">{evidence}</div>}
            {note && <div className="sd-sig-note">{note}</div>}
        </div>
    );
}

export function Signatures({ children }: { children: ReactNode }) {
    return <div className="sd-sigs">{children}</div>;
}

/** The sentence a locked instrument prints instead of an action. */
export function LockLine({ title, children }: { title: string; children: ReactNode }) {
    return (
        <div className="sd-lockline">
            <div>
                <div className="sd-ll-t">{title}</div>
                <div className="sd-ll-s">{children}</div>
            </div>
        </div>
    );
}
